/**
 * Payer Connections — canned copy.
 *
 * Verbatim strings from the PRD (AC-5, AC-6, AC-10, AC-11, AC-14, AC-15,
 * AC-16) and from the sprout assignment's rulings. Never hardcode
 * "patient"/"client" here — none of this copy is subject-language dependent.
 */

import type {
  BlockedParty,
  EnrollmentStatus,
  PayerAudience,
  PayerState,
  RailView,
} from './types';

/** AC-6: canned "what's happening" copy per status, used when Stedi returns no status_message/rejection_reason. */
export const CANNED_STATUS_COPY: Record<string, string> = {
  DRAFT: 'We are preparing this enrollment from your intake. Nothing needed from you yet.',
  CANCELED: 'This enrollment was canceled.',
  STEDI_ACTION_REQUIRED:
    'Submitted to the clearinghouse. Waiting on the clearinghouse to start processing.',
  PROVISIONING: 'The payer is processing the enrollment.',
  PROVIDER_ACTION_REQUIRED:
    'The payer needs information from the clinic — see the instructions in Stedi.',
  REJECTED: 'The payer rejected the enrollment. Check the details and resubmit.',
  /** LIVE with no claims yet (i.e. `ready`, not yet `live`). */
  LIVE: 'Connected. No claim confirmed as received by this payer yet.',
};

/** Fallback for a status Stedi returns that isn't in the table above (folds to `waiting` per AC-3). */
export const UNKNOWN_STATUS_COPY = 'Waiting on the payer.';

/** Resolve AC-6 copy for one rail: verbatim override first, else canned copy by status. */
export function resolveWhatsHappening(
  status: EnrollmentStatus,
  statusMessage?: string | null,
  rejectionReason?: string | null,
): string {
  if (statusMessage) return statusMessage;
  if (rejectionReason) return rejectionReason;
  return CANNED_STATUS_COPY[status] ?? UNKNOWN_STATUS_COPY;
}

/** GDN-3992: the count of open tasks on the rail beyond the one the row shows. */
export function moreTasksLabel(count: number): string {
  return `+${count} more ${count === 1 ? 'task' : 'tasks'}`;
}

/** GDN-3992: the toggle under a long task body. */
export const SHOW_MORE_LABEL = 'Show more';
export const SHOW_LESS_LABEL = 'Show less';

/** Pluralize "payer" — the only noun this feature counts. */
export function pluralizePayers(count: number): string {
  return count === 1 ? 'payer' : 'payers';
}

/**
 * The billing track heading (AC-5, restyled onto the Payer Handoff design).
 *
 * Priority: anything the reader must act on, then whether our team still has
 * payers to file, then "everything is moving". Every branch is derived from
 * folded state — no timestamps or counts the app cannot see.
 */
export function headline(
  counts: { needs_customer: number; ready: number; needs_us?: number },
  unknownCount = 0,
  audience: PayerAudience = 'clinic',
  enrollingCount?: number,
): string {
  const them = audience === 'staff' ? 'the clinic' : 'you';
  if (counts.needs_customer > 0) {
    const n = counts.needs_customer;
    return `${n} ${pluralizePayers(n)} ${n === 1 ? 'is' : 'are'} waiting on ${them}`;
  }
  if (unknownCount > 0) return 'Payer connections';
  const needsUs = counts.needs_us ?? 0;
  if (needsUs > 0 && enrollingCount !== undefined && needsUs === enrollingCount) {
    return audience === 'staff'
      ? "We're filing the clinic's payers"
      : "You're verified — we're filing your payers";
  }
  if (enrollingCount !== undefined && enrollingCount > 0) return 'All payers are moving';
  return audience === 'staff' ? 'Nothing is waiting on the clinic' : 'Nothing is waiting on you';
}

export interface SubheadCounts {
  /** Payers still enrolling (not live). */
  enrollingCount: number;
  liveCount: number;
  /** Confirmed-ready payers only — never `counts.ready`, which includes unknown evidence. */
  readyCount: number;
  /** Payers whose most severe rail is still with our team. */
  needsUsCount: number;
  unknownCount?: number;
}

/**
 * The line under the heading: how many payers there are and where they sit.
 * While our team still has payers to file, that number leads; afterwards the
 * reader cares about what is billable and what is fully live.
 */
export function subhead({
  enrollingCount,
  liveCount,
  readyCount,
  needsUsCount,
  unknownCount = 0,
}: SubheadCounts): string {
  if (unknownCount > 0)
    return `${enrollingCount} ${pluralizePayers(enrollingCount)} enrolling · ${liveCount} confirmed live`;
  const total = enrollingCount + liveCount;
  const head = `${total} ${pluralizePayers(total)}`;
  if (needsUsCount > 0)
    return `${head} · ${needsUsCount} still with our team to file · ${readyCount} ready for a first claim`;
  return `${head} · ${readyCount} ready for a first claim · ${liveCount} fully live`;
}

/** AC-4 footer, left side. */
export function footer(liveCount: number, unknownCount = 0): string {
  if (unknownCount > 0) return `${liveCount} ${pluralizePayers(liveCount)} confirmed live`;
  return liveCount > 0
    ? `${liveCount} ${pluralizePayers(liveCount)} live — managed in the claim workbench`
    : 'No payers live yet';
}

/** The amber banner above the table: the only rows that stop moving without the reader. */
export function needsYouBannerTitle(count: number, audience: PayerAudience = 'clinic'): string {
  const them = audience === 'staff' ? 'the clinic' : 'you';
  return count === 1
    ? `One payer needs something from ${them}`
    : `${count} payers need something from ${them}`;
}

export function needsYouBannerBody(audience: PayerAudience = 'clinic'): string {
  const them = audience === 'staff' ? 'the clinic' : 'you';
  return `Everything else is on us or with the payer — these are the only rows that stop moving without ${them}.`;
}

/** Rail names the reader recognizes; an unknown rail keeps its transaction type. */
export const RAIL_LABELS: Record<string, string> = { '837P': 'Claims', '835': 'Payments' };

/**
 * What each rail's folded state is doing, in the row's second line. The words
 * follow a plural subject ("Claims need you"), and the blocked-on-the-clinic
 * rung names the clinic in the staff view, as the state chip does.
 */
export const RAIL_STATE_WORDS: Record<Exclude<PayerState, 'live'>, string> = {
  needs_us: 'with our team',
  needs_customer: 'need you',
  waiting: 'pending',
  ready: 'ready',
  canceled: 'canceled',
};

const STAFF_RAIL_STATE_WORDS: Partial<Record<Exclude<PayerState, 'live'>, string>> = {
  needs_customer: 'need the clinic',
};

const RAIL_ORDER = ['837P', '835'];

/**
 * "Claims pending · Payments ready" — one segment per rail the payer actually
 * has. A rail that is absent says nothing: a payer with no 835 has no payment
 * rail to report, which is not the same as one that has not started.
 */
export function railsLine(rails: RailView[], audience: PayerAudience = 'clinic'): string {
  return rails
    .slice()
    .sort((a, b) => railOrder(a.transactionType) - railOrder(b.transactionType))
    .map(rail => {
      const label = RAIL_LABELS[rail.transactionType] ?? rail.transactionType;
      const word =
        (audience === 'staff' ? STAFF_RAIL_STATE_WORDS[rail.state] : undefined) ??
        RAIL_STATE_WORDS[rail.state];
      return `${label} ${word}`;
    })
    .join(' · ');
}

function railOrder(transactionType: string): number {
  const index = RAIL_ORDER.indexOf(transactionType);
  return index === -1 ? RAIL_ORDER.length : index;
}

/** The design's rule for a number we cannot source: show nothing, never a made-up zero. */
export const NO_VALUE = '—';

/** AC-10 fine print, in the design's wording: what a blank age means, and what this track is not. */
export const FINE_PRINT =
  "Blank waiting times mean we don't have a reliable start date for that row yet — we'd rather show nothing than a wrong number. This covers the practice's connection to each payer; clinician credentialing is its own track.";

/** AC-11: zero enrollment rows — the design's empty card. */
export const EMPTY_STATE_TITLE = 'No payers listed yet';

export const EMPTY_STATE_COPY =
  'JotPsych is setting up your payers. Nothing is waiting on you yet.';

/**
 * The passthrough failed. Not an acceptance criterion — AC-11 covers the
 * empty organization, not an unreachable clearinghouse — but the section
 * has to say something, and the PRD's rule is no snackbar spam: an inline
 * message with a Retry that refetches.
 */
export const LOAD_ERROR_COPY = 'We could not reach the clearinghouse just now.';

/** Label on the inline error's refetch control. */
export const RETRY_LABEL = 'Retry';

/** AC-16 roster chip-strip line: "N payers · N live". */
export function rosterPayerLine(payerCount: number, liveCount: number): string {
  return `${payerCount} ${pluralizePayers(payerCount)} · ${liveCount} live`;
}

/** AC-14 "Blocked on" chip labels. */
export const BLOCKED_ON_LABELS: Record<BlockedParty, string> = {
  JOTPSYCH: 'On JotPsych',
  CUSTOMER: 'Awaiting clinic',
  PAYER: 'Waiting on payer',
  NONE: 'Nothing outstanding',
};

/** AC-15 tooltip for a null days-blocked value. */
export const NO_TIMESTAMP_TOOLTIP = 'No timestamp on this org to measure from';

/** One consolidated notice for unavailable claim evidence. */
export function unavailableClaimCheckNotice(payerNames: string[]): string {
  return `We couldn’t check whether a claim has reached ${payerNames.join(', ')}. Connection statuses are shown above.`;
}

export const CHECK_AGAIN_LABEL = 'Check again';
